import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEra } from '../context/EraContext';
import { whatsappUrl } from '../config/contact';
import './WhatsAppButton.css';

// Eras whose homepage already owns the bottom-right corner with a contact
// widget of its own (2006 IM window, 2020 Intercom-style messenger) — those
// route to WhatsApp themselves, so a second floating button would collide.
const HOME_ERAS_WITH_OWN_FAB = new Set([2006, 2020]);
// Eras whose homepage pins something to the bottom-right edge (a status bar,
// tab bar or back-to-top button); the button rides above it.
const HOME_ERAS_WITH_BOTTOM_BAR = new Set([1996, 2016, 'superapp', 'future']);

export const WhatsAppGlyph = (props) => (
  <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false" {...props}>
    <path
      fill="currentColor"
      d="M16.02 3C8.84 3 3 8.83 3 16c0 2.29.6 4.53 1.74 6.5L3 29l6.68-1.75A13 13 0 0 0 16.02 29C23.2 29 29 23.17 29 16S23.2 3 16.02 3Zm0 23.8c-1.98 0-3.92-.53-5.61-1.53l-.4-.24-3.96 1.04 1.06-3.86-.26-.4A10.78 10.78 0 0 1 5.2 16c0-5.96 4.85-10.8 10.82-10.8 5.96 0 10.8 4.84 10.8 10.8 0 5.96-4.84 10.8-10.8 10.8Zm5.93-8.09c-.33-.16-1.93-.95-2.23-1.06-.3-.11-.52-.16-.74.16-.22.33-.85 1.06-1.04 1.28-.19.22-.38.25-.71.08-.33-.16-1.38-.51-2.63-1.62-.97-.87-1.63-1.94-1.82-2.27-.19-.33-.02-.5.14-.67.15-.15.33-.38.49-.57.16-.19.22-.33.33-.55.11-.22.05-.41-.03-.57-.08-.16-.74-1.78-1.01-2.44-.27-.64-.54-.55-.74-.56h-.63c-.22 0-.57.08-.87.41-.3.33-1.14 1.12-1.14 2.72 0 1.6 1.17 3.15 1.33 3.37.16.22 2.3 3.51 5.57 4.92.78.34 1.39.54 1.86.69.78.25 1.49.21 2.05.13.63-.09 1.93-.79 2.2-1.55.27-.76.27-1.41.19-1.55-.08-.14-.3-.22-.63-.38Z"
    />
  </svg>
);

const WhatsAppButton = () => {
  const { t, i18n } = useTranslation();
  const { era } = useEra();
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);

  // Enter once the visitor has shown intent (scrolled or stayed a moment),
  // rather than greeting them with a sales widget on first paint.
  useEffect(() => {
    if (visible) return undefined;
    const show = () => setVisible(true);
    const onScroll = () => { if (window.scrollY > window.innerHeight * 0.25) show(); };
    const timer = window.setTimeout(show, 2200);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
    };
  }, [visible]);

  // The English site speaks to recruiters and engineers: email and LinkedIn,
  // no floating chat. WhatsApp stays on the Portuguese side.
  if (i18n.language !== 'pt' || pathname.startsWith('/admin')) return null;
  const onHome = pathname === '/';
  if (onHome && era && HOME_ERAS_WITH_OWN_FAB.has(era.year)) return null;

  const classes = ['wa-float'];
  if (visible) classes.push('is-visible');
  if (onHome && era && HOME_ERAS_WITH_BOTTOM_BAR.has(era.year)) classes.push('is-raised');
  // 2126's controls wrap to two lines on phones, so it needs extra clearance.
  if (onHome && era && era.year === 'future') classes.push('is-raised-tall');

  return (
    <div className={classes.join(' ')}>
      <a
        className="wa-button"
        href={whatsappUrl(i18n.language)}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('wa_aria')}
      >
        <span className="wa-icon"><WhatsAppGlyph /></span>
        <span className="wa-label">{t('wa_label')}</span>
      </a>
    </div>
  );
};

export default WhatsAppButton;
